import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibField, KibFieldGroup, KibSelect } from '@chewy/kib-fields-new-react'
import { KibModal } from '@chewy/kib-overlays-react'
import * as R from 'ramda'
import {
  type BasePuiDialogProps,
  Defaults,
  moment,
  type Nil,
  useFields,
} from '@pbt/pbt-ui-components'

import { Type } from '~/api/graphql/generated/types'
import ClientCard from '~/components/common/ContactTargetSlot/ClientCard'
import ContactCard from '~/components/common/ContactTargetSlot/ContactCard'
import { BaseRoute } from '~/constants/routes'
import {
  clearMoveMessagesError,
  moveMessagesToNewConversation,
} from '~/store/actions/conversations'
import { fetchUpcomingEvents } from '~/store/actions/scheduler'
import {
  getIsMovingMessages,
  getMovedConversationId,
  getMoveMessagesError,
} from '~/store/reducers/conversations'
import {
  getPatientName,
  getPatientsIsLoading,
  getPatientsList,
} from '~/store/reducers/patients'
import {
  getSchedulerIsLoading,
  getSchedulerUpcomingAppointmentsList,
} from '~/store/reducers/scheduler'
import { getMultipleTimetableEvents } from '~/store/reducers/timetable'
import { getUser } from '~/store/reducers/users'
import type { Conversation } from '~/types'
import { convertToKibSuperSelectOption } from '~/utils/communicationsUtils'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'

import AppointmentSelect from '../common/AppointmentSelect'
import BusinessCard from './right-rail/BusinessCard'
import DirectCommunicationCard from './right-rail/DirectCommunicationCard'
import { getRecipientIdAndType } from './utils'

import styles from './NewThreadReplyDialog.module.scss'

export interface NewThreadReplyDialogProps extends BasePuiDialogProps {
  conversation: Conversation
  messageId: string
}

const NewThreadReplyDialog = ({
  open,
  onClose,
  conversation,
  messageId,
}: NewThreadReplyDialogProps) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation(['Common', 'Communications', 'Dialogs'])
  const movedConversationId = useSelector(getMovedConversationId)
  const moveError = useSelector(getMoveMessagesError)
  const isMoving = useSelector(getIsMovingMessages)
  const [hasInitiatedMove, setHasInitiatedMove] = useState(false)

  useEffect(() => {
    if (open) {
      dispatch(clearMoveMessagesError())
    }
  }, [open, dispatch])

  const {
    recipient,
    client: clientId,
    patient: patientId,
    title,
    event,
  } = conversation
  const businessTimezone = useCurrentBusinessTimezone()
  const client = useSelector(getUser(clientId))
  const initialPatientName = useSelector(getPatientName(patientId))
  const [patientName, setPatientName] = useState<string | Nil>(
    initialPatientName,
  )
  const patients = useSelector(getPatientsList(client?.patients))
  const isLoadingPatients = useSelector(getPatientsIsLoading)
  const isLoadingAppointments = useSelector(getSchedulerIsLoading)
  const appointmentsList =
    useSelector(getSchedulerUpcomingAppointmentsList) ?? []
  const events = useSelector(getMultipleTimetableEvents(appointmentsList))
  const appointments = useMemo(() => {
    const validEvents = events.filter(Boolean)
    return convertToKibSuperSelectOption(validEvents)
  }, [events])
  const { id: recipientId, type: recipientType } =
    getRecipientIdAndType(recipient)
  const {
    fields: { messageField, patientField, linkedAppointmentField },
    validate,
    reset,
  } = useFields([
    {
      name: 'messageField',
      validators: ['required'],
      initialValue: title,
    },
    {
      name: 'patientField',
      type: 'select',
      initialValue: patientId,
    },
    {
      name: 'linkedAppointmentField',
      type: 'select',
      initialValue: event?.id
        ? appointments.filter((appointment) => event.id === appointment.value)
        : [],
    },
  ])

  const handlePatientChange = (value: string) => {
    const selectedPatient = R.find(R.propEq('id', value), patients)
    setPatientName(selectedPatient?.name)
    patientField.setValue(value)
  }

  const handleClose = () => {
    dispatch(clearMoveMessagesError())
    reset()
    if (onClose) {
      onClose()
    }
  }

  const handleAdd = () => {
    if (validate()) {
      dispatch(
        moveMessagesToNewConversation(
          conversation.id,
          [messageId],
          messageField.value,
        ),
      )
      setHasInitiatedMove(true)
    }
  }

  useEffect(() => {
    if (hasInitiatedMove && movedConversationId) {
      navigate(`${BaseRoute.COMMUNICATIONS}/${movedConversationId}`, {
        state: { openMessageFormattingArea: true },
      })
      dispatch(clearMoveMessagesError())
      setHasInitiatedMove(false)
      reset()
      onClose?.()
    }
  }, [
    hasInitiatedMove,
    movedConversationId,
    navigate,
    onClose,
    reset,
    dispatch,
  ])

  useEffect(() => {
    if (Boolean(patientField.value) && clientId) {
      dispatch(
        fetchUpcomingEvents(
          clientId,
          patientField.value,
          moment
            .tz(businessTimezone)
            .subtract(Defaults.MAX_PAST_DAYS_FOR_APPOINTMENT, 'days')
            .format('YYYY-MM-DDTHH:mmZ'),
        ),
      )
    }
  }, [patientField.value])

  useEffect(() => {
    if (!isLoadingAppointments) {
      const selectedAppointment = event?.id
        ? appointments.filter((appointment) => event.id === appointment.value)
        : []
      linkedAppointmentField.setValue(selectedAppointment)
    }
  }, [isLoadingAppointments])

  const addButton = () => (
    <KibButtonNew disabled={isMoving} onClick={handleAdd}>
      {t('Common:ADD_ACTION')}
    </KibButtonNew>
  )

  return (
    <KibModal
      className={styles.newThreadReplyDialog}
      controls={addButton}
      initialFocus="#message-subject"
      open={open}
      title={t('Dialogs:NEW_THREAD_REPLY.TITLE')}
      onClose={handleClose}
    >
      {moveError && (
        <div className={styles.errorMessage} role="alert">
          {moveError}
        </div>
      )}
      <div className={styles.toFieldSection}>
        <span className="kib-field__label">{t('Common:TO')}</span>
        {recipientType === Type.Contact && recipientId && (
          <ContactCard contactId={recipientId} />
        )}
        {recipientType === Type.Client && recipientId && (
          <ClientCard clientId={recipientId} />
        )}
        {recipientType === Type.Business && recipientId && (
          <BusinessCard businessId={recipientId} />
        )}
        {recipientType === Type.Direct && (
          <DirectCommunicationCard
            recipientEmail={recipient?.email}
            recipientPhoneNumber={recipient?.phoneNumber}
          />
        )}
      </div>
      <KibFieldGroup className={styles.fieldGroup}>
        <KibField
          required
          id="message-subject"
          label={t('Common:MESSAGE')}
          value={messageField.value}
          onChange={(textEvent) =>
            messageField.setValue(textEvent.target.value)
          }
        />
        {(Boolean(patientField.value) || !R.isEmpty(patients)) && (
          <KibSelect
            disabled={isLoadingPatients}
            label={t('Common:PATIENT')}
            value={patientField.value}
            onChange={(selectEvent) =>
              handlePatientChange(selectEvent.target.value)
            }
          >
            <option hidden>{t('Common:ADD_PATIENT')}</option>
            {patients.map((patient) => (
              <option key={patient.id} value={patient.id}>
                {patient.name}
              </option>
            ))}
          </KibSelect>
        )}
        {Boolean(patientField.value) && (
          <AppointmentSelect
            appointmentField={linkedAppointmentField}
            appointments={appointments}
            patientName={patientName ?? ''}
          />
        )}
      </KibFieldGroup>
    </KibModal>
  )
}

export default NewThreadReplyDialog
